/**
 * Large text (2026-10-02 triage, item 13). The pixel display face in capitals with letter-spacing
 * does not scale: at 200% the tab labels collide and Profile reads "PRO…". Above LARGE_TEXT the tab
 * bar shows its icons only (the labels stay for screen readers), Profile shows its icon, and a
 * pushed screen's title wraps to two lines (src/styles.css, the "items 6, 8, 13" block). This sets
 * `data-text="large"` on <html> while the reader's text size is at or above the threshold.
 *
 * The size is read from hidden probes, measured once after load and again whenever they change
 * size (a ResizeObserver on them sees a text-size change, a rotation and a zoom alike):
 * - a 1rem span: a browser that applies the system text size through the root font size (Chrome
 *   on Android, a desktop's default font size, the a11y walk's `html { font-size: 200% }`);
 * - a 16px span: a browser that scales px text itself (text zoom) grows its line past 16;
 * - Safari's `-apple-system-body` font: iOS Dynamic Type (17px at the default size).
 * The largest ratio wins.
 *
 * For testing only: `localStorage["op.textSize"] = "large"` forces large text on, "normal" forces it
 * off (read on load).
 */
export const LARGE_TEXT = 1.3;
const OVERRIDE_KEY = "op.textSize";

/** The large-text decision from the measured ratio and the testing override. */
export function isLargeText(scale: number, override: string | null): boolean {
  if (override === "large") return true;
  if (override === "normal") return false;
  return Number.isFinite(scale) && scale >= LARGE_TEXT;
}

function readOverride(): string | null {
  try {
    return localStorage.getItem(OVERRIDE_KEY);
  } catch {
    return null;
  }
}

let started = false;

/** Starts watching the text size; safe to call more than once. */
export function watchTextSize(): void {
  if (started || typeof document === "undefined" || !document.body) return;
  started = true;
  const box = document.createElement("div");
  box.setAttribute("aria-hidden", "true");
  box.style.cssText = "position:absolute;left:-9999px;top:0;visibility:hidden;pointer-events:none;contain:layout style;";
  const probe = (css: string) => {
    const s = document.createElement("span");
    s.textContent = "Mg";
    s.style.cssText = `display:block;white-space:nowrap;line-height:1;${css}`;
    box.appendChild(s);
    return s;
  };
  const rem = probe("font-size:1rem;font-family:sans-serif;");
  const px = probe("font-size:16px;font-family:sans-serif;");
  const apple = typeof CSS !== "undefined" && CSS.supports?.("font", "-apple-system-body") ? probe("font:-apple-system-body;line-height:1;") : null;
  document.body.appendChild(box);
  const override = readOverride();
  const measure = () => {
    const ratios = [parseFloat(getComputedStyle(rem).fontSize) / 16, px.getBoundingClientRect().height / 16];
    if (apple) ratios.push(parseFloat(getComputedStyle(apple).fontSize) / 17);
    const scale = Math.max(...ratios.filter((r) => Number.isFinite(r) && r > 0));
    const large = isLargeText(scale, override);
    const root = document.documentElement;
    if (large) root.dataset.text = "large";
    else delete root.dataset.text;
  };
  measure();
  if (typeof ResizeObserver !== "undefined") {
    const observer = new ResizeObserver(measure);
    observer.observe(rem);
    observer.observe(px);
    if (apple) observer.observe(apple);
  }
  window.addEventListener("resize", measure);
}
